import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaRobot, FaTasks, FaTools, FaChartBar, FaEnvelope } from 'react-icons/fa';
import { motion, useInView } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Hero Section Animations
const heroVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: 'easeOut', delay },
  }),
};

const buttonVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } },
  hover: { scale: 1.05, boxShadow: '0px 8px 16px rgba(37, 150, 190, 0.2)' },
};

// Section Animations
const sectionVariants = {
  hidden: { opacity: 0 },
  visible: (delay = 0) => ({
    opacity: 1,
    transition: { duration: 0.8, ease: 'easeOut', delay },
  }),
};

const listVariants = {
  hidden: { opacity: 0, x: -50 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: 'easeOut', delay: i * 0.2 },
  }),
  hover: { y: -5, transition: { duration: 0.3 } },
};

const agentVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: 'easeOut', delay: i * 0.2, type: 'spring', bounce: 0.3 },
  }),
  hover: { scale: 1.05, boxShadow: '0px 8px 16px rgba(37, 150, 190, 0.2)' },
};

const iconVariants = {
  hidden: { opacity: 0, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, type: 'spring', stiffness: 100 },
  },
  hover: { scale: 1.1 },
};

const AIHire = () => {
  const heroRef = useRef(null);
  const whatIsRef = useRef(null);
  const capabilitiesRef = useRef(null);
  const agentsRef = useRef(null);
  const whyRef = useRef(null);
  const launchRef = useRef(null);

  const heroInView = useInView(heroRef, { margin: '-100px' });
  const whatIsInView = useInView(whatIsRef, { margin: '-100px' });
  const capabilitiesInView = useInView(capabilitiesRef, { margin: '-100px' });
  const agentsInView = useInView(agentsRef, { margin: '-100px' });
  const whyInView = useInView(whyRef, { margin: '-100px' });
  const launchInView = useInView(launchRef, { margin: '-100px' });

  // Letter-by-letter animation for headings
  const letterVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { duration: 0.5, delay: i * 0.05 },
    }),
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Import Google Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700&family=Inter:wght@400;500;600&display=swap"
        rel="stylesheet"
      />

      {/* Global styles for glow effect */}
      <style>
        {`
          .glow-button {
            animation: glow 1.5s ease-in-out infinite alternate;
          }
          @keyframes glow {
            from {
              box-shadow: 0 0 5px rgba(37, 150, 190, 0.4), 0 0 10px rgba(37, 150, 190, 0.3);
            }
            to {
              box-shadow: 0 0 15px rgba(37, 150, 190, 0.8), 0 0 25px rgba(37, 150, 190, 0.5);
            }
          }
        `}
      </style>

      <Header />

      {/* Hero Section */}
      <div
        className="relative max-w-7xl mx-auto px-6 py-24 flex flex-col items-center text-center mt-20 bg-gray-100 rounded-3xl shadow-md"
        ref={heroRef}
      >
        <motion.h1
          className="text-4xl md:text-7xl font-bold mb-6 leading-tight text-[#2596be] font-space-grotesk tracking-tight"
          initial="hidden"
          animate={heroInView ? 'visible' : 'hidden'}
          style={{ textShadow: '0 2px 4px rgba(0, 0, 0, 0.05)' }}
        >
          {'A.I Hire – Hire Your First A.I Today'.split('').map((char, i) => (
            <motion.span
              key={i}
              variants={letterVariants}
              custom={i}
              style={{ display: 'inline-block' }}
            >
              {char}
            </motion.span>
          ))}
        </motion.h1>
        <motion.p
          className="text-lg md:text-xl text-gray-900 mb-10 max-w-3xl font-inter leading-relaxed"
          variants={heroVariants}
          initial="hidden"
          animate={heroInView ? 'visible' : 'hidden'}
          custom={0.4}
        >
          A.I Hire by The A.I Union empowers businesses to seamlessly integrate AI agents into their teams. From sales to HR, these agents handle real tasks with ease.
        </motion.p>
        <motion.div
          variants={buttonVariants}
          initial="hidden"
          animate={heroInView ? 'visible' : 'hidden'}
          whileHover="hover"
          whileTap={{ scale: 0.95 }}
        >
          <Link
            to="/waitlist"
            className="bg-[#2596be] hover:bg-[#1e7a9b] text-white font-semibold py-4 px-10 rounded-lg transition-all duration-300 text-lg tracking-wide glow-button font-space-grotesk"
          >
            Join the Waitlist
          </Link>
        </motion.div>
      </div>

      {/* What is A.I Hire? */}
      <div className="py-24 bg-white" ref={whatIsRef}>
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h2
            className="text-3xl md:text-5xl font-bold mb-6 text-[#2596be] font-space-grotesk tracking-tight"
            initial="hidden"
            animate={whatIsInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
          >
            {'What is A.I Hire?'.split('').map((char, i) => (
              <motion.span
                key={i}
                variants={letterVariants}
                custom={i}
                style={{ display: 'inline-block' }}
              >
                {char}
              </motion.span>
            ))}
          </motion.h2>
          <motion.p
            className="text-lg text-gray-900 max-w-3xl mx-auto font-inter leading-relaxed"
            variants={sectionVariants}
            initial="hidden"
            animate={whatIsInView ? 'visible' : 'hidden'}
            custom={0.2}
          >
            A.I Hire simplifies the process of deploying AI agents to support your business operations. No tech expertise needed—just assign tasks and let the AI handle the rest.
          </motion.p>
        </div>
      </div>

      {/* What You’ll Be Able to Do */}
      <div className="max-w-7xl mx-auto px-6 py-24 bg-gray-100" ref={capabilitiesRef}>
        <motion.h2
          className="text-3xl md:text-5xl font-bold mb-12 text-[#2596be] text-center font-space-grotesk tracking-tight"
          initial="hidden"
          animate={capabilitiesInView ? 'visible' : 'hidden'}
          variants={sectionVariants}
        >
          {'What You’ll Be Able to Do'.split('').map((char, i) => (
            <motion.span
              key={i}
              variants={letterVariants}
              custom={i}
              style={{ display: 'inline-block' }}
            >
              {char}
            </motion.span>
          ))}
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[
            {
              icon: <FaRobot className="text-4xl text-[#2596be]" />,
              text: 'Access a library of specialized AI agents tailored for your needs.',
            },
            {
              icon: <FaTasks className="text-4xl text-[#2596be]" />,
              text: 'Delegate tasks like email drafting, resume screening, or scheduling.',
            },
            {
              icon: <FaTools className="text-4xl text-[#2596be]" />,
              text: 'Integrate with tools like Gmail, Slack, Notion, or your CRM effortlessly.',
            },
            {
              icon: <FaChartBar className="text-4xl text-[#2596be]" />,
              text: 'Track performance and outputs via a centralized dashboard.',
            },
          ].map((item, i) => (
            <motion.div
              key={i}
              className="flex items-start p-6 bg-white rounded-lg shadow-md transition-all duration-300 hover:scale-105 hover:shadow-lg"
              variants={listVariants}
              initial="hidden"
              animate={capabilitiesInView ? 'visible' : 'hidden'}
              custom={i}
              whileHover="hover"
            >
              <motion.div
                className="bg-gray-100 p-4 rounded-full mr-4"
                variants={iconVariants}
                whileHover="hover"
              >
                {item.icon}
              </motion.div>
              <p className="text-lg text-gray-600 font-inter leading-relaxed">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Example AI Agents */}
      <div className="py-24 bg-white" ref={agentsRef}>
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2
            className="text-3xl md:text-5xl font-bold mb-12 text-[#2596be] text-center font-space-grotesk tracking-tight"
            initial="hidden"
            animate={agentsInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
          >
            {'Example AI Agents'.split('').map((char, i) => (
              <motion.span
                key={i}
                variants={letterVariants}
                custom={i}
                style={{ display: 'inline-block' }}
              >
                {char}
              </motion.span>
            ))}
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Sales Assistant',
                desc: 'Automates email outreach, lead follow-ups, and CRM updates.',
              },
              {
                title: 'HR Assistant',
                desc: 'Screens resumes, schedules interviews, and manages candidate communication.',
              },
              {
                title: 'Marketing Assistant',
                desc: 'Crafts content, manages social media posts, and analyzes engagement.',
              },
              {
                title: 'Legal Assistant',
                desc: 'Drafts contracts, identifies risks, and simplifies legal documents.',
              },
              {
                title: 'Operations Assistant',
                desc: 'Handles admin tasks, organizes meetings, and keeps your team on track.',
              },
            ].map((agent, i) => (
              <motion.div
                key={i}
                className="p-6 bg-white rounded-lg shadow-md transition-all duration-300 hover:scale-105 hover:shadow-lg"
                variants={agentVariants}
                initial="hidden"
                animate={agentsInView ? 'visible' : 'hidden'}
                custom={i}
                whileHover="hover"
              >
                <motion.div variants={iconVariants} whileHover="hover">
                  <FaRobot className="text-4xl text-[#2596be] mb-4" />
                </motion.div>
                <h3 className="text-xl md:text-2xl font-semibold mb-2 text-[#375290] font-space-grotesk">{agent.title}</h3>
                <p className="text-lg text-gray-600 font-inter leading-relaxed">{agent.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Why It Matters */}
      <div className="max-w-7xl mx-auto px-6 py-24 bg-gray-100" ref={whyRef}>
        <motion.h2
          className="text-3xl md:text-5xl font-bold mb-6 text-[#2596be] text-center font-space-grotesk tracking-tight"
          initial="hidden"
          animate={whyInView ? 'visible' : 'hidden'}
          variants={sectionVariants}
        >
          {'Why It Matters'.split('').map((char, i) => (
            <motion.span
              key={i}
              variants={letterVariants}
              custom={i}
              style={{ display: 'inline-block' }}
            >
              {char}
            </motion.span>
          ))}
        </motion.h2>
        <motion.p
          className="text-lg text-gray-900 max-w-3xl mx-auto font-inter leading-relaxed text-center"
          variants={sectionVariants}
          initial="hidden"
          animate={whyInView ? 'visible' : 'hidden'}
          custom={0.2}
        >
          AI agents free your team from repetitive tasks, boosting efficiency and cutting costs. It’s the future of work—smart, scalable, and seamless.
        </motion.p>
      </div>

      {/* Launching Soon */}
      <div className="py-24 bg-white" ref={launchRef}>
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h2
            className="text-3xl md:text-5xl font-bold mb-6 text-[#2596be] font-space-grotesk tracking-tight"
            initial="hidden"
            animate={launchInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
          >
            {'Launching Soon'.split('').map((char, i) => (
              <motion.span
                key={i}
                variants={letterVariants}
                custom={i}
                style={{ display: 'inline-block' }}
              >
                {char}
              </motion.span>
            ))}
          </motion.h2>
          <motion.p
            className="text-lg text-gray-900 mb-8 font-inter leading-relaxed"
            variants={sectionVariants}
            initial="hidden"
            animate={launchInView ? 'visible' : 'hidden'}
            custom={0.2}
          >
            A.I Hire is coming soon to The A.I Union platform. Get early access by joining our waitlist today.
          </motion.p>
          <motion.div
            className="flex justify-center"
            variants={sectionVariants}
            initial="hidden"
            animate={launchInView ? 'visible' : 'hidden'}
            custom={0.4}
          >
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/waitlist"
                className="bg-[#2596be] hover:bg-[#1e7a9b] text-white font-semibold py-4 px-10 rounded-lg transition-all duration-300 text-lg tracking-wide glow-button font-space-grotesk"
              >
                Join the Waitlist
              </Link>
            </motion.div>
          </motion.div>
          <motion.p
            className="text-lg text-gray-900 mt-8 font-inter leading-relaxed"
            variants={sectionVariants}
            initial="hidden"
            animate={launchInView ? 'visible' : 'hidden'}
            custom={0.6}
          >
            Redefine hiring with intelligent tools. A.I Hire is on the horizon.
          </motion.p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AIHire;